import React from "react";
import {
  Puzzle,
  Gamepad2,
  Baby,
  Bike,
  Blocks,
  Brush,
  Dice5,
  Car,
  Joystick,
  Sofa,
  Sparkles,
  Palette,
  Smile,
  Drama,
  GraduationCap,
  PartyPopper,
  Dumbbell,
  Heart,
  Zap,
  Hammer,
  Home,
  Utensils,
  CircleDot,
  Users,
  Plane,
  Layers,
} from "lucide-react";

export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  parentId?: number | null;
  image?: any;
}

/* ──────────────────────────────────────────────
   ICON HELPER — keeps icon size consistent
   ────────────────────────────────────────────── */
const sz = 18; // default icon size (px)

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL TOYS CATEGORIES (900000xx)
   ────────────────────────────────────────────── */
export const idToysCategories: CategoryItem[] = [
  { id: 90000001, name: "Action Figures & Statues", icon: <Sparkles size={sz} /> },
  { id: 90000002, name: "Arts & Crafts", icon: <Palette size={sz} /> },
  { id: 90000003, name: "Baby & Toddler Toys", icon: <Baby size={sz} /> },
  { id: 90000004, name: "Building Toys", icon: <Blocks size={sz} /> },
  { id: 90000005, name: "Dolls & Accessories", icon: <Smile size={sz} /> },
  { id: 90000006, name: "Dress Up & Pretend Play", icon: <Drama size={sz} /> },
  { id: 90000007, name: "Kids' Electronics", icon: <Gamepad2 size={sz} /> },
  { id: 90000008, name: "Games", icon: <Dice5 size={sz} /> },
  { id: 90000009, name: "Grown-Up Toys", icon: <Sparkles size={sz} /> },
  { id: 90000010, name: "Hobbies", icon: <Puzzle size={sz} /> },
  { id: 90000011, name: "Kids' Furniture, Décor & Storage", icon: <Sofa size={sz} /> },
  { id: 90000012, name: "Learning & Education", icon: <GraduationCap size={sz} /> },
  { id: 90000013, name: "Novelty & Gag Toys", icon: <Smile size={sz} /> },
  { id: 90000014, name: "Party Supplies", icon: <PartyPopper size={sz} /> },
  { id: 90000015, name: "Puppets", icon: <Drama size={sz} /> },
  { id: 90000016, name: "Puzzles", icon: <Puzzle size={sz} /> },
  { id: 90000017, name: "Sports & Outdoor Play", icon: <Dumbbell size={sz} /> },
  { id: 90000018, name: "Stuffed Animals & Plush Toys", icon: <Heart size={sz} /> },
  { id: 90000019, name: "Remote Control & Play Vehicles", icon: <Car size={sz} /> },
  { id: 90000020, name: "Tricycles, Scooters & Wagons", icon: <Bike size={sz} /> },
  { id: 90000021, name: "Video Games", icon: <Joystick size={sz} /> },
];

/* ──────────────────────────────────────────────
   2️⃣ TOYS SUBCATEGORIES (90000xxx)
   ────────────────────────────────────────────── */
export const idToysSubcategories: CategoryItem[] = [
  // Action Figures
  { id: 90000101, parentId: 90000001, name: "Superheroes", icon: <Zap size={sz} /> },
  { id: 90000102, parentId: 90000001, name: "Anime & Collectibles", icon: <Sparkles size={sz} /> },

  // Arts & Crafts
  { id: 90000201, parentId: 90000002, name: "Drawing & Painting", icon: <Brush size={sz} /> },
  { id: 90000202, parentId: 90000002, name: "DIY Craft Kits", icon: <Palette size={sz} /> },

  // Baby & Toddler
  { id: 90000301, parentId: 90000003, name: "Sensory Toys", icon: <Baby size={sz} /> },
  { id: 90000302, parentId: 90000003, name: "Early Learning Toys", icon: <GraduationCap size={sz} /> },

  // Building Toys
  { id: 90000401, parentId: 90000004, name: "Blocks & Bricks", icon: <Blocks size={sz} /> },
  { id: 90000402, parentId: 90000004, name: "Construction Sets", icon: <Hammer size={sz} /> },

  // Dolls
  { id: 90000501, parentId: 90000005, name: "Fashion Dolls", icon: <Smile size={sz} /> },
  { id: 90000502, parentId: 90000005, name: "Doll Houses & Sets", icon: <Home size={sz} /> },

  // Pretend Play
  { id: 90000601, parentId: 90000006, name: "Costumes", icon: <Drama size={sz} /> },
  { id: 90000602, parentId: 90000006, name: "Play Kitchens & Tools", icon: <Utensils size={sz} /> },

  // Games
  { id: 90000801, parentId: 90000008, name: "Board Games", icon: <Dice5 size={sz} /> },
  { id: 90000802, parentId: 90000008, name: "Card Games", icon: <Layers size={sz} /> },

  // Outdoor Play
  { id: 90001701, parentId: 90000017, name: "Ball Games", icon: <CircleDot size={sz} /> },
  { id: 90001702, parentId: 90000017, name: "Ride-On Toys", icon: <Bike size={sz} /> },

  // Plush
  { id: 90001801, parentId: 90000018, name: "Teddy Bears", icon: <Heart size={sz} /> },
  { id: 90001802, parentId: 90000018, name: "Character Plush", icon: <Smile size={sz} /> },

  // RC Vehicles
  { id: 90001901, parentId: 90000019, name: "RC Cars", icon: <Car size={sz} /> },
  { id: 90001902, parentId: 90000019, name: "RC Drones & Planes", icon: <Plane size={sz} /> },

  // Video Games
  { id: 90002101, parentId: 90000021, name: "Console Games", icon: <Gamepad2 size={sz} /> },
  { id: 90002102, parentId: 90000021, name: "PC & Digital Games", icon: <Joystick size={sz} /> },
];

/* ──────────────────────────────────────────────
   3️⃣ TOYS PRODUCTS (90010101+)
   ────────────────────────────────────────────── */
export const idToysProducts: CategoryItem[] = [
  // Board Games
  { id: 90080101, parentId: 90000801, name: "Strategy Games", icon: <Dice5 size={sz} /> },
  { id: 90080102, parentId: 90000801, name: "Family Games", icon: <Users size={sz} /> },

  // Arts & Crafts
  { id: 90020101, parentId: 90000201, name: "Coloring Sets", icon: <Palette size={sz} /> },
  { id: 90020102, parentId: 90000201, name: "Paint Kits", icon: <Brush size={sz} /> },

  // Building
  { id: 90040101, parentId: 90000401, name: "Plastic Blocks", icon: <Blocks size={sz} /> },
  { id: 90040102, parentId: 90000401, name: "Magnetic Tiles", icon: <Layers size={sz} /> },

  // Outdoor
  { id: 900170101, parentId: 90001701, name: "Football Sets", icon: <CircleDot size={sz} /> },
  { id: 900170201, parentId: 90001702, name: "Kids Scooters", icon: <Bike size={sz} /> },

  // Video Games
  { id: 900210101, parentId: 90002101, name: "PlayStation Games", icon: <Gamepad2 size={sz} /> },
  { id: 900210102, parentId: 90002101, name: "Nintendo Games", icon: <Joystick size={sz} /> },
];